import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Snackbar,
  FormControlLabel,
  Switch,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Divider,
  Collapse,
} from '@mui/material';
import {
  Person as PersonIcon,
  Security,
  Save,
  CheckCircle,
  HealthAndSafety,
  Groups as GroupsIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Bloodtype as BloodtypeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import api from '../../config/axiosConfig';
import { FIELD_LIMITS } from '../../constants/fieldLimits';
import userService from '../../services/userService';
import JourneyStepper from '../common/JourneyStepper';

const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const BRIGADISTA_ROLES = [
  'No aplica',
  'Jefe de Brigada',
  'Coordinador de Brigada',
  'Brigadista de Evacuación',
  'Brigadista de Primeros Auxilios',
  'Brigadista de Prevención de Incendios',
  'Brigadista de Búsqueda y Rescate',
  'Brigadista de Comunicación',
];
const EMPTY_PERSON = {
  fullName: '', curp: '', bloodType: '', position: '', brigadistaRole: 'No aplica', allergiesDisease: '',
};

const LlenarInformacion = () => {
  const [loading, setLoading] = useState(true);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [quotations, setQuotations] = useState([]); // todas las cotizaciones elegibles de la empresa
  const [selectedQuotationId, setSelectedQuotationId] = useState('');
  const [quotation, setQuotation] = useState(null);
  const [entities, setEntities] = useState([]);
  const [entityEdits, setEntityEdits] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [entityExpandedById, setEntityExpandedById] = useState({});

  // Personal state keyed by entityId
  const [personalExpandedByEntity, setPersonalExpandedByEntity] = useState({});
  const [personalLists, setPersonalLists] = useState({});
  const [newPersonByEntity, setNewPersonByEntity] = useState({});
  const [personErrorByEntity, setPersonErrorByEntity] = useState({});

  useEffect(() => {
    loadQuotations();
  }, []);

  useEffect(() => {
    if (selectedQuotationId) {
      const target = quotations.find(q => q.id === selectedQuotationId);
      setQuotation(target || null);
      loadEntities(selectedQuotationId);
    }
  }, [selectedQuotationId]);

  const loadQuotations = async () => {
    try {
      setLoading(true);
      const me = await userService.getCurrentUser();
      const companyId = me?.company?.id;
      if (!companyId) return;

      const res = await api.get('/quotations');
      const all = Array.isArray(res.data) ? res.data : (res.data.content || []);
      const mine = all.filter(q => q.companyId === companyId);

      // Solo tiene sentido llenar información de cotizaciones ya aceptadas; si no hay
      // ninguna aceptada aún, se muestran las pendientes para no dejar al usuario sin nada.
      const accepted = mine.filter(q => q.status === 'ACCEPTED');
      const eligible = accepted.length > 0 ? accepted : mine.filter(q => q.status === 'PENDING');

      eligible.sort((a, b) => new Date(b.acceptedAt || b.createdAt || 0) - new Date(a.acceptedAt || a.createdAt || 0));

      setQuotations(eligible);
      if (eligible.length > 0) {
        setSelectedQuotationId(eligible[0].id);
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Error al cargar tus cotizaciones', severity: 'error' });
      setLoading(false);
    }
  };

  const loadEntities = async (quotationId) => {
    try {
      setLoadingEntities(true);
      const entRes = await api.get(`/quotations/${quotationId}/entities`);
      const ents = Array.isArray(entRes.data) ? entRes.data : [];
      setEntities(ents);

      const edits = {};
      const lists = {};
      const newPersons = {};

      const entityExpanded = {};
      const expanded = {};
      ents.forEach(e => {
        entityExpanded[e.id] = true;
        expanded[e.id] = false;
        edits[e.id] = {
          inspectorContact: e.inspectorContact || '',
          requiresPpe: e.requiresPpe || false,
          ppeDetails: e.ppeDetails || '',
          scheduledDate: e.scheduledDate || '',
          scheduledTimeStart: e.scheduledTimeStart || '',
        };
        lists[e.id] = [];
        newPersons[e.id] = { ...EMPTY_PERSON };
      });

      setEntityEdits(edits);
      setEntityExpandedById(entityExpanded);
      setPersonalExpandedByEntity(expanded);
      setNewPersonByEntity(newPersons);
      setPersonErrorByEntity({});

      // Cargar personal por ente
      await Promise.all(
        ents.map(async (e) => {
          try {
            const personalRes = await api.get(`/personal/entity/${e.id}`);
            const data = Array.isArray(personalRes.data) ? personalRes.data : [];
            lists[e.id] = data.map(p => ({ ...p, _localId: p.id }));
          } catch {
            // endpoint pendiente de implementación
          }
        })
      );

      setPersonalLists(lists);
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Error al cargar los entes de la cotización', severity: 'error' });
    } finally {
      setLoadingEntities(false);
      setLoading(false);
    }
  };

  const handleEntityChange = (entityId, field, value) => {
    setEntityEdits(prev => ({
      ...prev,
      [entityId]: { ...prev[entityId], [field]: value },
    }));
  };

  const handlePersonChange = (entityId, field, value) => {
    setNewPersonByEntity(prev => ({
      ...prev,
      [entityId]: { ...prev[entityId], [field]: value },
    }));
    if (field === 'fullName') {
      setPersonErrorByEntity(prev => ({ ...prev, [entityId]: '' }));
    }
  };

  const handleAddPerson = (entityId) => {
    const person = newPersonByEntity[entityId] || { ...EMPTY_PERSON };
    if (!person.fullName.trim()) {
      setPersonErrorByEntity(prev => ({ ...prev, [entityId]: 'El nombre completo es obligatorio.' }));
      return;
    }
    setPersonalLists(prev => ({
      ...prev,
      [entityId]: [...(prev[entityId] || []), { ...person, _localId: Date.now() }],
    }));
    setNewPersonByEntity(prev => ({ ...prev, [entityId]: { ...EMPTY_PERSON } }));
    setPersonErrorByEntity(prev => ({ ...prev, [entityId]: '' }));
  };

  const handleRemovePerson = (entityId, _localId) => {
    setPersonalLists(prev => ({
      ...prev,
      [entityId]: (prev[entityId] || []).filter(p => p._localId !== _localId),
    }));
  };

  const saveAll = async () => {
    setSavingAll(true);
    try {
      await Promise.all(
        entities.map(e =>
          api.put(`/quotations/entities/${e.id}/inspection-info`, entityEdits[e.id] || {})
        )
      );
      await Promise.all(
        entities
          .filter(e => (personalLists[e.id] || []).length > 0)
          .map(e => api.post(`/personal/entity/${e.id}/sync`, personalLists[e.id]))
      );
      setSnackbar({ open: true, message: 'Información guardada correctamente.', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Error al guardar. Verifica los datos e intenta de nuevo.', severity: 'error' });
    } finally {
      setSavingAll(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f5f7fa', minHeight: '100vh', py: 4 }}>
      <Box sx={{ maxWidth: '1400px', mx: 'auto', px: 3 }}>
        <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Security sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                Preparación para Inspección
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Complete la información requerida por cada ente antes de la visita de inspección
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ mb: 4 }}>
          <JourneyStepper />
        </Box>

        {quotations.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            No tienes cotizaciones aceptadas. Una vez que aceptes tu cotización en el historial, podrás completar la información para la inspección.
          </Alert>
        ) : (
          <>
            {quotations.length > 1 && (
              <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, bgcolor: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: '#24292e' }}>
                  Tienes {quotations.length} cotizaciones — elige cuál vas a preparar
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Cotización</InputLabel>
                  <Select
                    value={selectedQuotationId}
                    label="Cotización"
                    onChange={(e) => setSelectedQuotationId(e.target.value)}
                    sx={{ borderRadius: 2 }}
                  >
                    {quotations.map(q => (
                      <MenuItem key={q.id} value={q.id}>
                        {q.invoiceFolio || `COT-${String(q.id).padStart(6, '0')}`}
                        {q.serviceName ? ` — ${q.serviceName}` : ''}
                        {q.acceptedAt ? ` · aceptada ${new Date(q.acceptedAt).toLocaleDateString('es-MX')}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Paper>
            )}

            {quotation && (
              <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 3, borderRadius: 2 }}>
                <strong>{quotation.invoiceFolio || `COT-${String(quotation.id).padStart(6, '0')}`}</strong> — {quotation.businessName}. Por favor completa la información de cada ente antes de la fecha de inspección.
              </Alert>
            )}

            {loadingEntities ? (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
              </Box>
            ) : entities.length === 0 && (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                Esta cotización no tiene entes configurados. Contacta a tu ejecutivo de CONEM.
              </Alert>
            )}

            {!loadingEntities && entities.map((entity, idx) => {
              const edits = entityEdits[entity.id] || {};
              const entityExpanded = entityExpandedById[entity.id] !== false;
              const personalExpanded = !!personalExpandedByEntity[entity.id];
              const personalList = personalLists[entity.id] || [];
              const newPerson = newPersonByEntity[entity.id] || { ...EMPTY_PERSON };
              const personError = personErrorByEntity[entity.id] || '';

              return (
                <Paper
                  key={entity.id}
                  elevation={0}
                  sx={{ mb: 3, borderRadius: 3, bgcolor: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', overflow: 'hidden' }}
                >
                  {/* ─── Header de ente (colapsable) ─── */}
                  <Box
                    onClick={() => setEntityExpandedById(prev => ({ ...prev, [entity.id]: !prev[entity.id] }))}
                    sx={{ p: 3, pb: 2, borderBottom: entityExpanded ? '2px solid #e1e4e8' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', cursor: 'pointer', userSelect: 'none', '&:hover': { bgcolor: '#f8fafc' } }}
                  >
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                        <Chip
                          label={entity.entityType === 'MATRIZ' ? 'Matriz' : 'Sucursal'}
                          color={entity.entityType === 'MATRIZ' ? 'primary' : 'secondary'}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#24292e' }}>
                          {entity.entityName}
                        </Typography>
                        {personalList.length > 0 && (
                          <Chip label={`${personalList.length} persona${personalList.length !== 1 ? 's' : ''}`} size="small" color="info" variant="outlined" sx={{ fontWeight: 600 }} />
                        )}
                      </Box>
                      {entity.address && (
                        <Typography variant="body2" color="text.secondary">
                          {entity.address}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Ente {idx + 1} de {entities.length}
                      </Typography>
                      {entityExpanded ? <ExpandLessIcon sx={{ color: 'text.secondary' }} /> : <ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
                    </Box>
                  </Box>

                  <Collapse in={entityExpanded}>
                  {/* ─── Información de inspección ─── */}
                  <Box sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          label="Persona que nos acompañará en el recorrido de inspección"
                          value={edits.inspectorContact || ''}
                          onChange={(e) => handleEntityChange(entity.id, 'inspectorContact', e.target.value)}
                          variant="outlined"
                          inputProps={{ maxLength: FIELD_LIMITS.inspectorContact }}
                          helperText="El responsable del inmueble o un representante autorizado debe estar presente durante toda la inspección."
                          InputProps={{
                            startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />,
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                      </Grid>

                      {entity.hasWorkOrderDetail ? (
                        <>
                          {/* Misma fecha/hora que Agenda de Servicios (work_order_details): editable
                              desde aquí y desde "Iniciar PIPC" -- siempre corresponden porque ambas
                              pantallas leen/escriben el mismo registro. */}
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                              fullWidth
                              type="date"
                              label="Fecha de inicio de inspección"
                              value={edits.scheduledDate || ''}
                              onChange={(e) => handleEntityChange(entity.id, 'scheduledDate', e.target.value)}
                              variant="outlined"
                              InputLabelProps={{ shrink: true }}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                          </Grid>

                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                              fullWidth
                              type="time"
                              label="Hora de inicio de inspección"
                              value={edits.scheduledTimeStart || ''}
                              onChange={(e) => handleEntityChange(entity.id, 'scheduledTimeStart', e.target.value)}
                              variant="outlined"
                              InputLabelProps={{ shrink: true }}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                          </Grid>
                        </>
                      ) : (
                        <Grid size={{ xs: 12 }}>
                          <Box sx={{
                            p: 2, borderRadius: 2, border: '1px solid #ffe0b2', bgcolor: '#fff8e1',
                            display: 'flex', alignItems: 'center', gap: 1.5,
                          }}>
                            <Security sx={{ color: 'warning.main', fontSize: 20 }} />
                            <Typography variant="body2" fontWeight={600} color="text.secondary">
                              Aún no se ha generado la orden de trabajo de esta cotización. En cuanto Fuse la genere, podrás capturar aquí la fecha y hora.
                            </Typography>
                          </Box>
                        </Grid>
                      )}

                      <Grid size={{ xs: 12 }}>
                        <Box
                          sx={{
                            p: 2.5,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: edits.requiresPpe ? 'warning.main' : 'divider',
                            bgcolor: edits.requiresPpe ? '#fffbf0' : 'transparent',
                            transition: 'all 0.3s ease',
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Switch
                                checked={!!edits.requiresPpe}
                                onChange={(e) => handleEntityChange(entity.id, 'requiresPpe', e.target.checked)}
                                color="warning"
                              />
                            }
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <HealthAndSafety sx={{ color: edits.requiresPpe ? 'warning.main' : 'text.secondary', fontSize: 20 }} />
                                <Typography fontWeight={600}>
                                  ¿Se requiere equipo de protección personal (EPP)?
                                </Typography>
                              </Box>
                            }
                          />
                          {edits.requiresPpe && (
                            <TextField
                              fullWidth
                              multiline
                              rows={2}
                              label="Especifique el equipo de protección requerido"
                              value={edits.ppeDetails || ''}
                              onChange={(e) => handleEntityChange(entity.id, 'ppeDetails', e.target.value)}
                              variant="outlined"
                              inputProps={{ maxLength: FIELD_LIMITS.ppeDetails }}
                              placeholder="Ej: Casco, guantes de nitrilo, lentes de seguridad, zapatos dieléctricos..."
                              sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                          )}
                        </Box>
                      </Grid>

                    </Grid>
                  </Box>

                  {/* ─── Listado de Personal del ente ─── */}
                  <Box sx={{ borderTop: '1px solid #e1e4e8' }}>
                    {/* Subheader colapsable */}
                    <Box
                      onClick={() => setPersonalExpandedByEntity(prev => ({ ...prev, [entity.id]: !prev[entity.id] }))}
                      sx={{ px: 3, py: 2, background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <GroupsIcon sx={{ fontSize: 26 }} />
                        <Box>
                          <Typography variant="subtitle1" fontWeight={700}>Listado de Personal</Typography>
                          <Typography variant="caption" sx={{ opacity: 0.85 }}>
                            Personal del inmueble <strong>{entity.entityName}</strong> con datos médicos y rol de brigada
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {personalList.length > 0 && (
                          <Chip
                            label={`${personalList.length} persona${personalList.length !== 1 ? 's' : ''}`}
                            size="small"
                            sx={{ bgcolor: 'rgba(255,255,255,0.22)', color: 'white', fontWeight: 700 }}
                          />
                        )}
                        {personalExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </Box>
                    </Box>

                    <Collapse in={personalExpanded}>
                    <Box sx={{ p: 3 }}>
                      {/* Formulario agregar persona */}
                      <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2, bgcolor: '#f8fafc' }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, color: '#1565c0', display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <PersonAddIcon fontSize="small" /> Agregar persona
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <TextField
                              fullWidth size="small"
                              label="Nombre completo *"
                              value={newPerson.fullName}
                              onChange={e => handlePersonChange(entity.id, 'fullName', e.target.value)}
                              error={!!personError}
                              helperText={personError}
                              inputProps={{ maxLength: FIELD_LIMITS.uipcWorkerName }}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField
                              fullWidth size="small"
                              label="CURP"
                              value={newPerson.curp}
                              onChange={e => handlePersonChange(entity.id, 'curp', e.target.value.toUpperCase())}
                              inputProps={{ maxLength: FIELD_LIMITS.curp }}
                              helperText={newPerson.curp && newPerson.curp.length !== 18 ? `${newPerson.curp.length}/18 caracteres` : ' '}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontFamily: 'monospace' } }}
                            />
                          </Grid>
                          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                            <FormControl fullWidth size="small">
                              <InputLabel>T. Sangre</InputLabel>
                              <Select
                                value={newPerson.bloodType}
                                label="T. Sangre"
                                onChange={e => handlePersonChange(entity.id, 'bloodType', e.target.value)}
                                sx={{ borderRadius: 2 }}
                              >
                                <MenuItem value=""><em>—</em></MenuItem>
                                {BLOOD_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                            <TextField
                              fullWidth size="small"
                              label="Puesto"
                              value={newPerson.position}
                              onChange={e => handlePersonChange(entity.id, 'position', e.target.value)}
                              inputProps={{ maxLength: FIELD_LIMITS.uipcPosition }}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 8, md: 4 }}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Brigadista asignado</InputLabel>
                              <Select
                                value={newPerson.brigadistaRole}
                                label="Brigadista asignado"
                                onChange={e => handlePersonChange(entity.id, 'brigadistaRole', e.target.value)}
                                sx={{ borderRadius: 2 }}
                              >
                                {BRIGADISTA_ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12, md: 8 }}>
                            <TextField
                              fullWidth size="small"
                              label="Alergias y/o enfermedades"
                              value={newPerson.allergiesDisease}
                              onChange={e => handlePersonChange(entity.id, 'allergiesDisease', e.target.value)}
                              inputProps={{ maxLength: FIELD_LIMITS.uipcAllergies }}
                              placeholder="Ej: Alergia a la penicilina, diabetes tipo 2..."
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                          </Grid>
                          <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                              variant="contained"
                              startIcon={<PersonAddIcon />}
                              onClick={() => handleAddPerson(entity.id)}
                              sx={{ textTransform: 'none', borderRadius: 2, px: 3, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}
                            >
                              Agregar
                            </Button>
                          </Grid>
                        </Grid>
                      </Paper>

                      {/* Tabla de personal */}
                      {personalList.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                          <GroupsIcon sx={{ fontSize: 52, opacity: 0.2, mb: 1, display: 'block', mx: 'auto' }} />
                          <Typography variant="body2">Aún no se han agregado personas al listado.</Typography>
                          <Typography variant="caption">Usa el formulario de arriba para comenzar.</Typography>
                        </Box>
                      ) : (
                        <Box sx={{ overflowX: 'auto', mb: 2 }}>
                          <Table size="small" sx={{ '& th': { fontWeight: 700, bgcolor: '#f0f4f8', whiteSpace: 'nowrap' } }}>
                            <TableHead>
                              <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Nombre completo</TableCell>
                                <TableCell>CURP</TableCell>
                                <TableCell align="center">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                                    <BloodtypeIcon fontSize="small" sx={{ color: '#e53935' }} /> Sangre
                                  </Box>
                                </TableCell>
                                <TableCell>Puesto</TableCell>
                                <TableCell>Brigadista</TableCell>
                                <TableCell>Alergias / Enfermedades</TableCell>
                                <TableCell align="center">Eliminar</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {personalList.map((p, i) => (
                                <TableRow key={p._localId} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                  <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>{i + 1}</TableCell>
                                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{p.fullName}</TableCell>
                                  <TableCell>
                                    <Typography variant="caption" sx={{ fontFamily: 'monospace', letterSpacing: 0.5 }}>
                                      {p.curp || '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    {p.bloodType ? (
                                      <Chip label={p.bloodType} size="small" sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 700 }} />
                                    ) : '—'}
                                  </TableCell>
                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.position || '—'}</TableCell>
                                  <TableCell>
                                    {p.brigadistaRole && p.brigadistaRole !== 'No aplica' ? (
                                      <Chip label={p.brigadistaRole} size="small" color="primary" variant="outlined" />
                                    ) : (
                                      <Typography variant="caption" color="text.secondary">No aplica</Typography>
                                    )}
                                  </TableCell>
                                  <TableCell sx={{ maxWidth: 240 }}>
                                    <Typography variant="caption" sx={{ color: p.allergiesDisease ? '#555' : 'text.disabled' }}>
                                      {p.allergiesDisease || '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Tooltip title="Eliminar persona">
                                      <IconButton size="small" color="error" onClick={() => handleRemovePerson(entity.id, p._localId)}>
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      )}

                      <Divider sx={{ my: 2 }} />
                      <Typography variant="caption" color="text.secondary">
                        {personalList.length} persona{personalList.length !== 1 ? 's' : ''} en el listado de {entity.entityName}
                      </Typography>
                    </Box>
                    </Collapse>
                  </Box>
                  </Collapse>
                </Paper>
              );
            })}
          </>
        )}

        {/* Botón único guardar todo */}
        {quotation && entities.length > 0 && (
          <Box sx={{ position: 'sticky', bottom: 24, display: 'flex', justifyContent: 'flex-end', mt: 3, pointerEvents: 'none' }}>
            <Button
              variant="contained"
              size="large"
              onClick={saveAll}
              disabled={savingAll}
              startIcon={savingAll ? <CircularProgress size={20} color="inherit" /> : <Save />}
              sx={{
                pointerEvents: 'all',
                textTransform: 'none',
                borderRadius: 3,
                px: 5,
                py: 1.5,
                fontWeight: 700,
                fontSize: '1rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 8px 24px rgba(102,126,234,0.45)',
                '&:hover': { background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)', boxShadow: '0 12px 28px rgba(102,126,234,0.55)' },
              }}
            >
              {savingAll ? 'Guardando...' : 'Guardar todo'}
            </Button>
          </Box>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar(p => ({ ...p, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar(p => ({ ...p, open: false }))}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default LlenarInformacion;
